// Pagina /fatturazione — portale fatturazione (sostituisce il GSheet "Fatturazione").
// Round 3: colonne fisse/spostabili/ridimensionabili (useColumnLayout, persistite),
// una colonna per ogni dato (columns.tsx), export Excel, modifica pre-emissione,
// legenda fonti. Spec: docs/superpowers/specs/2026-07-14-fatturazione-portale-design.md.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import {
  downloadExportXlsx,
  fetchUltimoBatch,
  fetchWorklist,
  markSentBulk,
  syncNow,
  type WorklistFilters,
} from "./api";
import { useDebounced } from "@/shared/useDebounced";
import type { BatchStatus, CampoData, Counts, Proposal, SortField, StornoScoperto } from "./types";
import { WORKLIST_COLUMNS, type ColumnDef } from "./components/columns";
import { useColumnLayout } from "./useColumnLayout";
import { EurekaConnection } from "./components/EurekaConnection";
import { CampoCerca } from "@/shared/ui/ChipFiltro";
import { StrumentiFatture } from "./components/StrumentiFatture";
import { CreateDocumentsDialog } from "./components/CreateDocumentsDialog";
import { DateRangeFilter, RANGE_VUOTO, type DateRange } from "./components/DateRangeFilter";
import { EditDialog } from "./components/EditDialog";
import { MarkDialog } from "./components/MarkDialog";
import { ManualDialog } from "./components/ManualDialog";
import { NcConfirmDialog } from "./components/NcConfirmDialog";
import { ExcludeDialog } from "./components/ExcludeDialog";
import { rowToneSdi } from "./components/SdiAlertBadge";
import { propsSenzaPermesso, usePermessi } from "@/shared/auth/permessi";
import { AzioniBarra, TastoBarra } from "@/shared/layout/MobileTopBar";
import { useIsMobile } from "@/shared/useMediaQuery";
import { ProposalCardMobile } from "./components/ProposalCardMobile";
import { AzioniRiga } from "./components/AzioniRiga";
import {
  CodeLavoro,
  COUNT_KEY,
  ETICHETTE_TAB,
  poiDellaCoda,
  type TabKey,
} from "./components/CodeLavoro";
import { StorniScoperti } from "./components/StorniScoperti";
import { FogliTelefono } from "./components/FogliTelefono";

// Le code (Da fatturare, Storni, Da inviare, In attesa) e l'archivio (Tutte,
// Storico, Escluse) stanno in ``CodeLavoro``. «Tutte» resta il default (Davide
// 28/07): un tab-parcheggio che nasconde righe è il modo in cui una fattura si
// dimentica. «Storni» c'è dal 26/10 (18114942-A): prima gli storni stavano
// mescolati in «Da fatturare», e quello escluso non lo vedeva più nessuno.

// Da fatturare = il lavoro aperto, emissioni E note di credito (`in_revisione`
// serve alle NC che aspettano la conferma del rimborso); In attesa = i due
// stati pendenti; Da inviare/Storico/Escluse mostrano ANCHE le NC; «Tutte» =
// nessun filtro stato, entrambi i kind.
function tabParams(tab: TabKey): { status?: string; kind?: string } {
  if (tab === "da_fatturare")
    return { status: "da_fatturare,in_revisione", kind: "emission,credit_note" };
  if (tab === "in_attesa")
    return { status: "in_attesa_spedizione,in_attesa_pagamento", kind: "emission,credit_note" };
  if (tab === "storni") return { kind: "credit_note" };
  if (tab === "da_inviare") return { status: "da_inviare", kind: "emission,credit_note" };
  if (tab === "storico") return { status: "fatturata", kind: "emission,credit_note" };
  if (tab === "escluse") return { status: "esclusa", kind: "emission,credit_note" };
  if (tab === "tutte") return { kind: "emission,credit_note" };
  return { status: tab };
}

// Quante righe si chiedono al backend. Il valore lo dichiariamo NOI invece di
// lasciarlo al default del server, perché serve anche a sapere quando la lista
// è arrivata PIENA: `counts` non è filtrato per ricerca/canale/periodo, quindi
// con un filtro attivo il confronto «N di M» non si può fare — ma «sono
// arrivate esattamente 100 righe» resta vero in ogni caso, ed è l'unico segnale
// che c'è dell'altro oltre la centesima.
const LIMITE_WORKLIST = 100;

// Ogni quanto si rilegge la banda «Emissione in corso» quando il dialog è
// chiuso. Cinque secondi e non 1,2 come nel dialog: qui si muove solo un
// contatore, e la GET rilegge tutto il diario del batch.
const BANDA_POLL_MS = 5000;

// Colonne su cui il primo click ordina DAL PIÙ RECENTE / PIÙ GRANDE. Su una
// data o un importo il crescente non è mai quello che si vuole vedere per
// primo: cliccare «Data fatt.» e ricevere gennaio è un click sprecato ogni
// volta. Il testo invece si legge naturalmente dalla A.
// La colonna «azioni» presa dalla definizione condivisa: la card del telefono
// passa da `renderCell` come la tabella, quindi eredita lo stesso fieldset del
// permesso invece di doverlo ricordare.
const AZIONI_COL = WORKLIST_COLUMNS.find((c) => c.key === "azioni") as ColumnDef;

const SORT_DESC_FIRST = new Set<SortField>([
  "invoice_date",
  "order_date",
  "document_date",
  "gross_expected",
]);

/**
 * Cosa si può selezionare dipende da cosa si può fare con la selezione, e nella
 * pagina le azioni di massa sono due:
 *  - nel tab «Da inviare» il documento ESISTE già e manca solo la trasmissione
 *    allo SdI → si selezionano proprio le righe che un numero ce l'hanno;
 *  - altrove si crea il documento → si selezionano quelle che NON ce l'hanno.
 * Una casella che non porta mai a un'azione è rumore, quindi non si disegna.
 */
function selezionabile(r: Proposal, tab: TabKey): boolean {
  if (tab === "da_inviare") return r.status === "da_inviare";
  return !r.document_number && r.status !== "esclusa" && r.status !== "fatturata";
}

export function FatturazionePage() {
  // Cosmesi: il gate vero è sul backend (403). Qui si evita di mostrare attivo
  // un tasto che darebbe errore — e si dice PERCHÉ è spento.
  const puoFatturare = usePermessi().puoScrivere("fatturazione");
  // Le props del gate calcolate UNA volta: `{}` se puoi scrivere (spread
  // trasparente), altrimenti `{disabled, title}` col motivo in italiano. Si
  // spargono IN CODA agli altri attributi, così sovrascrivono `disabled` e
  // `title` solo quando il permesso manca davvero.
  const senzaPermesso = propsSenzaPermesso(puoFatturare, "fatturazione");

  const [rows, setRows] = useState<Proposal[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [scoperti, setScoperti] = useState<StornoScoperto[]>([]);
  // ``?q=`` dalla pagina ordine («Apri in Fatturazione»): la lista parte gia'
  // filtrata su quell'ordine. Solo il valore iniziale — poi il campo e' suo.
  const [searchParams] = useSearchParams();
  // ?tab=storni: il link della campanella «rimborsi senza storno».
  const [tab, setTab] = useState<TabKey>(() =>
    searchParams.get("tab") === "storni" ? "storni" : "tutte",
  );
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const debouncedQuery = useDebounced(query, 300);
  const [channelFilter, setChannelFilter] = useState("");
  const [channels, setChannels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ordinamento server-side: default DATA FATTURAZIONE DECRESCENTE (#2, Davide
  // 21/07). Era crescente per la «regola #14» — che però governa l'ORDINE DI
  // EMISSIONE dentro Eureka (numero e data devono restare monotoni), non
  // l'ordine di lettura di una lista di 636 righe. A schermo serve vedere per
  // primo quello di oggi; la creazione documenti riordina per data da sola.
  const [sort, setSort] = useState<SortField>("invoice_date");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [range, setRange] = useState<DateRange>(RANGE_VUOTO);
  // Su quale data lavora il periodo. Default ORDINE (Davide 06/08): è la data
  // con cui si guarda un mese di vendite. Chi deve quadrare la liquidazione IVA
  // passa a «Data doc», dove una fattura vale il mese in cui è stata emessa.
  const [campoData, setCampoData] = useState<CampoData>("order");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  // Emissione avviata e non ancora finita, anche se partita da un'altra
  // finestra o prima di un refresh: senza questo chi chiude il dialog perde di
  // vista un batch che sta scrivendo documenti fiscali veri.
  const [batchAperto, setBatchAperto] = useState<BatchStatus | null>(null);
  /** batch_id da riaprire nel dialog (vista avanzamento, niente anteprima). */
  const [batchDaGuardare, setBatchDaGuardare] = useState<string | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  // Id incrementale per scartare le risposte di load() superate: le load
  // post-azione (runRowAction/sync/dialog) NON passano dall'AbortController
  // dell'effect, quindi una risposta lenta di un tab precedente potrebbe
  // sovrascrivere quello corrente (last-write-by-completion, bug-hunt 2026-07-17).
  const reqIdRef = useRef(0);

  const layout = useColumnLayout(WORKLIST_COLUMNS);

  const [markDialog, setMarkDialog] = useState<{
    proposal: Proposal;
    forceStatus?: "fatturata";
  } | null>(null);
  const [excludeTarget, setExcludeTarget] = useState<Proposal | null>(null);
  const [ncTarget, setNcTarget] = useState<Proposal | null>(null);
  const [editTarget, setEditTarget] = useState<Proposal | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [rowBusyId, setRowBusyId] = useState<number | null>(null);
  // Telefono: canale e periodo stanno in un foglio, gli strumenti (Excel,
  // legenda, riga manuale) in un altro — la tabella diventa una pila di card.
  const isMobile = useIsMobile();
  const [filtriAperti, setFiltriAperti] = useState(false);
  const [strumentiAperti, setStrumentiAperti] = useState(false);

  // Filtri della vista corrente in UN posto solo: la tabella e l'export Excel
  // leggono lo stesso oggetto, così «esporta quello che vedo» resta vero anche
  // quando si aggiunge un filtro (il periodo, oggi).
  const filters: WorklistFilters = useMemo(
    () => ({
      ...tabParams(tab),
      q: debouncedQuery,
      channel: channelFilter,
      sort,
      direction,
      from_date: range.from,
      to_date: range.to,
      date_field: campoData,
      limit: LIMITE_WORKLIST,
    }),
    [tab, debouncedQuery, channelFilter, sort, direction, range.from, range.to, campoData],
  );

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const reqId = ++reqIdRef.current; // questa load diventa la "corrente"
      setLoading(true);
      setError(null);
      try {
        const data = await fetchWorklist(filters, signal);
        // Scarta se abortita O superata da una load più recente (race).
        if (signal?.aborted || reqId !== reqIdRef.current) return;
        setRows(data.items);
        setCounts(data.counts);
        setScoperti(data.storni_scoperti ?? []);
        // La selezione va POTATA su ciò che è davvero in tabella. Dopo
        // un'azione di riga («Escludi», «In attesa», «Riclassifica») la riga
        // esce dal tab ma il suo id restava dentro `selected`: la banda diceva
        // «8 righe selezionate» e al dialog ne arrivavano 7, perché quello
        // riceve `selectedRows` (l'intersezione con le righe caricate). Due
        // numeri diversi in due schermate consecutive, proprio dove il
        // conteggio è la difesa contro il click distratto. Si ritorna `prev`
        // quando non cambia niente, altrimenti ogni load rifarebbe render.
        // Il metro è `selezionabile`, NON la sola presenza: in «Tutte» una riga
        // appena esclusa o fatturata resta a schermo ma perde la casella —
        // impossibile da deselezionare, e comunque dentro i `selectedRows` che
        // arrivano al dialog di creazione documenti.
        setSelected((prev) => {
          const spuntabili = new Set(
            data.items.filter((r) => selezionabile(r, tab)).map((r) => r.id),
          );
          const potata = new Set([...prev].filter((id) => spuntabili.has(id)));
          return potata.size === prev.size ? prev : potata;
        });
        // Canali: unione monotòna tra le load (mai restringere). ``data.items``
        // è troncato (cap backend 100) e filtrato per q/tab, quindi derivare i
        // canali SOLO da esso fa sparire canali validi dal filtro. Non c'è una
        // fonte canali stabile lato server (``counts`` non è per-canale, nessun
        // endpoint dedicato) → si accumula quanto visto, così un canale resta
        // selezionabile una volta apparso.
        // ponytail: se servisse un elenco autorevole, esporlo dai counts backend
        // invece di accumularlo lato client.
        setChannels((prev) =>
          [...new Set([...prev, ...data.items.map((r) => r.channel).filter(Boolean)])].sort(),
        );
      } catch (err) {
        if (signal?.aborted || reqId !== reqIdRef.current) return;
        setError(prettifyApiError(err));
        setRows([]);
        setCounts(null);
      } finally {
        if (!signal?.aborted && reqId === reqIdRef.current) setLoading(false);
      }
    },
    // `tab` non aggiunge fetch: `filters` contiene già `tabParams(tab)` e
    // cambia identità insieme a lui.
    [filters, tab],
  );

  /** Legge l'ultimo batch e tiene la banda solo se sta ancora girando.
   *  ponytail: nessun polling QUI — a seguire il batch ci pensa il dialog; la
   *  pagina si limita a ricontrollare al mount e alla chiusura della finestra,
   *  che sono i due momenti in cui il dato può essere cambiato senza saperlo. */
  const caricaBatchAperto = useCallback(async (signal?: AbortSignal) => {
    try {
      const b = await fetchUltimoBatch(signal);
      if (!signal?.aborted) setBatchAperto(b?.in_corso ? b : null);
    } catch {
      // La banda è un di più: se la lettura fallisce si resta com'era, senza
      // occupare il banner degli errori con qualcosa che non blocca nulla.
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void caricaBatchAperto(ctrl.signal);
    return () => ctrl.abort();
  }, [caricaBatchAperto]);

  // La banda era uno scatto fermo al mount: chi avviava un batch e chiudeva
  // subito la finestra leggeva «0/20» per venti minuti, e continuava a leggerlo
  // anche a emissione finita da un pezzo — o si aspetta a vuoto, o si crede che
  // si sia piantato qualcosa. Si rilegge solo mentre la banda c'è davvero, e
  // solo col dialog chiuso: quando è aperto è lui a seguire il batch (1,2 s), e
  // due polling sullo stesso batch sarebbero il doppio del carico per lo stesso
  // numero. Quando il batch finisce, `caricaBatchAperto` azzera la banda e
  // questo effect si spegne da solo.
  const bandaDaAggiornare = batchAperto !== null && !createOpen && !batchDaGuardare;
  useEffect(() => {
    if (!bandaDaAggiornare) return;
    // Catena di `setTimeout`, non `setInterval`: sotto carico questa risposta
    // puo' superare i 5 s dell'intervallo, e l'intervallo partirebbe lo stesso
    // accumulando giri sul server (l'handler non si ferma quando il browser
    // molla la fetch). Stesso motivo per cui CreateDocumentsDialog usa la
    // catena, e stessa lezione del 2026-09-08 sul pool esaurito.
    let timer: number | null = null;
    let attivo = true;
    const giro = async (): Promise<void> => {
      await caricaBatchAperto();
      if (attivo) timer = window.setTimeout(() => void giro(), BANDA_POLL_MS);
    };
    timer = window.setTimeout(() => void giro(), BANDA_POLL_MS);
    return () => {
      attivo = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [bandaDaAggiornare, caricaBatchAperto]);

  function toggleSort(field: SortField) {
    if (sort === field) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setDirection(SORT_DESC_FIRST.has(field) ? "desc" : "asc");
    }
  }

  async function runSyncNow() {
    setSyncBusy(true);
    setSyncMsg(null);
    setError(null);
    try {
      const r = await syncNow();
      setSyncMsg(
        `Sync completata: ${r.detect.created} nuove, ${r.nc.created} NC, ` +
          `${r.backfill.filled} arricchite, ${r.backfill.generic_excluded} GENERIC escluse.`,
      );
      await load();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setSyncBusy(false);
    }
  }

  /** Scarica l'XLSX della vista corrente (stessi filtri/ordinamento) con stato
   *  busy sul bottone ed errori gestiti (via fetch/blob, non più ``<a href>``). */
  async function downloadXlsx() {
    if (exportBusy) return;
    setExportBusy(true);
    setError(null);
    try {
      await downloadExportXlsx(filters);
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setExportBusy(false);
    }
  }

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Cambiare tab/ricerca/canale/periodo azzera la selezione. Non è pignoleria:
  // una selezione che sopravvive a un cambio di filtro porta a premere «Crea
  // documenti» su righe che non sono più a schermo — cioè a emettere documenti
  // fiscali alla cieca. La selezione vale solo per ciò che si sta guardando.
  useEffect(() => {
    setSelected(new Set());
  }, [filters]);

  const selectableRows = useMemo(() => rows.filter((r) => selezionabile(r, tab)), [rows, tab]);
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  const allSelected = selectableRows.length > 0 && selected.size === selectableRows.length;

  function toggleRow(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** «Segna inviate» in blocco sul tab «Da inviare»: registra in OM il F11 che
   *  è stato fatto a mano in Eureka. Non trasmette nulla — nessun tool MCP lo
   *  può fare (vedi `markSentBulk`). */
  async function runMarkSent() {
    setSendBusy(true);
    setError(null);
    try {
      const { inviate, errori } = await markSentBulk(selectedRows);
      setSyncMsg(
        errori.length === 0
          ? `${inviate} righe segnate come inviate.`
          : `${inviate} segnate, ${errori.length} non riuscite (${errori[0]?.errore ?? ""}).`,
      );
      setSelected(new Set());
      await load();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setSendBusy(false);
    }
  }

  function toggleAll() {
    setSelected((prev) =>
      prev.size === selectableRows.length ? new Set() : new Set(selectableRows.map((r) => r.id)),
    );
  }

  async function runRowAction(id: number, action: (id: number) => Promise<Proposal>) {
    if (rowBusyId !== null) return; // una sola azione-riga alla volta (rowBusyId è singolo)
    setRowBusyId(id);
    setError(null);
    try {
      await action(id);
      await load();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setRowBusyId(null);
    }
  }

  function renderCell(col: ColumnDef, r: Proposal) {
    // UN solo cancello per TUTTE le azioni di riga: un `fieldset` disabilitato
    // spegne nativamente ogni controllo che contiene (e accende le varianti
    // `disabled:` di Tailwind). Spegnerli uno per uno vuol dire dimenticarsi il
    // prossimo che si aggiunge — ed è esattamente com'è nato questo buco: il
    // gate copriva il solo tasto «Fattura», mentre ogni azione di riga è una
    // POST in area `fatturazione` (403 dal backend al click).
    // `min-w-0` perché il fieldset nasce con `min-inline-size: min-content`: in
    // una colonna stretta impedirebbe alle azioni di andare a capo.
    if (col.key === "azioni") {
      return (
        <fieldset className="min-w-0" {...senzaPermesso}>
          <AzioniRiga
            r={r}
            rowBusyId={rowBusyId}
            runRowAction={(id, action) => void runRowAction(id, action)}
            onMark={(p, forceStatus) => setMarkDialog({ proposal: p, forceStatus })}
            onExclude={setExcludeTarget}
            onNc={setNcTarget}
            onEdit={setEditTarget}
            onEsito={(messaggio) => {
              setError(null);
              setSyncMsg(messaggio);
            }}
            onErrore={setError}
          />
        </fieldset>
      );
    }
    if (col.key === "sel") {
      if (!selezionabile(r, tab)) return null;
      return (
        <input
          type="checkbox"
          checked={selected.has(r.id)}
          onChange={() => toggleRow(r.id)}
          aria-label={`Seleziona ${r.order_ref ?? r.customer_name}`}
          className="size-[15px] cursor-pointer accent-accent-2 align-middle"
        />
      );
    }
    return col.render ? col.render(r) : null;
  }

  const totalWidth = layout.visible.reduce((acc, c) => acc + layout.widthOf(c.key), 0);

  // Banner "mostrate N di M": il backend rende max 100 righe (le più vecchie) ma
  // il badge del tab mostra il totale reale → le righe oltre la 100 sarebbero
  // invisibili senza avviso. Mostrato SOLO senza ricerca/filtro canale attivi,
  // perché ``counts`` è il totale per-tab NON filtrato per q/canale
  // (``repo.counts()`` lato server): con un filtro attivo M non descrive più le
  // righe viste e il confronto sarebbe fuorviante.
  const tabTotal = counts?.[COUNT_KEY[tab]] ?? null;
  // Il periodo entra in questa condizione insieme a ricerca e canale: `counts`
  // è il totale per-tab NON filtrato, quindi con un filtro data attivo il
  // confronto direbbe «mostrate 12 di 636» — vero ma fuorviante, sembrerebbe un
  // troncamento quando è il filtro che sta funzionando.
  const filtroAttivo = Boolean(debouncedQuery || channelFilter || range.from || range.to);
  // ...ma tacere è peggio che dire una cosa imprecisa. Nel flusso principale —
  // filtrare per mese e fatturare — con un filtro attivo NON c'era alcun
  // segnale del troncamento: si premeva «seleziona tutto» (che prende solo le
  // righe caricate), si creavano 100 documenti e si considerava chiuso un mese
  // che ne aveva 240. La lista arrivata piena è un fatto vero anche senza
  // `counts`, e quello si può sempre dire.
  // Il numero sul tasto Filtri del telefono: canale e periodo, cioè quello che
  // sta nel foglio e quindi non si vede finché non lo si apre.
  const filtriFoglio = (channelFilter ? 1 : 0) + (range.from || range.to ? 1 : 0);
  const listaPiena = rows.length >= LIMITE_WORKLIST;
  const isTruncated = listaPiena || (tabTotal != null && !filtroAttivo && tabTotal > rows.length);

  // Barra di selezione: compare solo quando serve, e sta SOPRA la tabella
  // (l'azione è a portata di occhio insieme al conteggio di cosa colpirà).
  // Il conteggio è la difesa più economica contro il click distratto.
  // Sul telefono la stessa barra si appoggia in fondo, sopra la navigazione,
  // dove arriva il pollice (come in Ordini).
  const barraSelezione = selected.size > 0 && (
    <div
      role="status"
      className="mb-2 flex flex-wrap items-center gap-3 rounded-full border border-accent/40 bg-accent-soft py-1.5 pl-4 pr-1.5 max-md:sticky max-md:bottom-3 max-md:z-30 max-md:mb-0 max-md:mt-2 max-md:flex-nowrap max-md:rounded-2xl max-md:border-0 max-md:bg-ink max-md:py-2 max-md:pl-4 max-md:pr-2 max-md:shadow-[0_8px_24px_rgba(31,27,22,0.28)]"
    >
      {/* Frase intera e non pezzi incollati: «riga»+«e» dava «2 rigae». */}
      <span className="text-[12.5px] font-medium text-ink max-md:min-w-0 max-md:flex-1 max-md:text-[15px] max-md:font-bold max-md:text-white">
        {selected.size} {selected.size === 1 ? "riga selezionata" : "righe selezionate"}
      </span>
      <button
        type="button"
        onClick={() => setSelected(new Set())}
        className="text-[12px] text-ink-2 underline-offset-2 hover:text-ink hover:underline max-md:h-11 max-md:px-2 max-md:text-[14px] max-md:font-semibold max-md:text-white"
      >
        {isMobile ? "Annulla" : "Deseleziona"}
      </button>
      {tab === "da_inviare" ? (
        <button
          type="button"
          disabled={sendBusy}
          onClick={() => void runMarkSent()}
          title="Segna come trasmesse allo SdI le righe selezionate (il F11 in Eureka lo fai tu: qui si registra che è stato fatto)"
          {...senzaPermesso}
          className="ml-auto h-[32px] rounded-full border border-accent bg-accent-2 px-4 text-[12.5px] font-semibold text-white disabled:opacity-50 max-md:ml-0 max-md:h-11 max-md:rounded-xl max-md:px-5 max-md:text-[15px] max-md:font-semibold"
        >
          {sendBusy ? "Segno…" : "Segna inviate"}
        </button>
      ) : (
        // Con un'emissione in corso il tasto è spento: l'anteprima
        // dichiarerebbe «pronte» proprio le righe che il batch sta
        // scrivendo in Eureka in quell'istante, perché il collegamento
        // proposta↔documento viene committato solo a fine giro. Il backend
        // risponderebbe comunque 409 alla conferma, ma solo DOPO aver
        // mostrato un'anteprima che dice il contrario della verità.
        <button
          type="button"
          disabled={batchAperto !== null || !puoFatturare}
          onClick={() => setCreateOpen(true)}
          title={
            // 🔴 Il permesso viene PRIMA del batch in corso: a chi non può
            // fatturare va detto perché il tasto è spento, e «c'è
            // un'emissione in corso» lo manderebbe ad aspettare una cosa
            // che non cambierà mai il suo caso.
            !puoFatturare
              ? "Non hai i permessi per emettere documenti. Puoi consultare la fatturazione, ma non operarci."
              : batchAperto
                ? "C'è già un'emissione in corso: aspetta che finisca (i documenti si creano uno alla volta)"
                : "Mostra l'anteprima dei documenti da fatturare in Eureka (non crea nulla finché non confermi)"
          }
          className="ml-auto h-[32px] rounded-full border border-accent bg-accent-2 px-4 text-[12.5px] font-semibold text-white disabled:opacity-50 max-md:ml-0 max-md:h-11 max-md:rounded-xl max-md:px-5 max-md:text-[15px] max-md:font-semibold"
        >
          Fattura
        </button>
      )}
    </div>
  );

  return (
    <main className="flex-1 overflow-y-auto px-7 pb-8 pt-5 scroll-paper max-md:px-3 max-md:pt-3">
      {/* Redesign 27/10: una plancia sola come in Ordini; sul telefono i filtri nel foglio. */}
      {isMobile ? (
        <div className="mb-3">
          <CodeLavoro tab={tab} onTab={setTab} counts={counts} scoperti={scoperti.length} />
        </div>
      ) : (
        <section
          aria-label="Filtri e strumenti"
          className="mb-4 flex flex-col gap-3 rounded-2xl border border-line bg-card p-4 shadow-[0_1px_2px_rgba(31,27,22,0.04)]"
        >
          <div className="flex flex-wrap items-center gap-2">
            <CampoCerca
              value={query}
              onChange={setQuery}
              placeholder="Cerca ordine, cliente o numero documento…"
              ariaLabel="Cerca proposte"
              className="min-w-[220px] max-w-[400px] flex-1"
            />
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              aria-label="Filtra per canale"
              className={`h-[34px] cursor-pointer rounded-full border bg-card px-3 text-[12.5px] text-ink outline-none transition-colors hover:border-ink-4 ${
                channelFilter ? "border-ink font-semibold" : "border-line-2 font-medium"
              }`}
            >
              <option value="">Tutti i canali</option>
              {channels.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <DateRangeFilter
              value={range}
              onChange={setRange}
              campo={campoData}
              onCampoChange={setCampoData}
            />
            <Link
              to="/fatturazione/corrispettivi"
              className="ml-auto inline-flex h-[34px] items-center rounded-full px-2 text-[12.5px] font-semibold text-ink underline-offset-4 hover:bg-paper-2 hover:underline"
              title="Il file mensile dei corrispettivi Amazon per l'amministrazione"
            >
              Corrispettivi Amazon →
            </Link>
          </div>

          <CodeLavoro tab={tab} onTab={setTab} counts={counts} scoperti={scoperti.length} />

          <StrumentiFatture
            syncBusy={syncBusy}
            onSync={() => void runSyncNow()}
            onRigaManuale={() => setManualOpen(true)}
            senzaPermesso={senzaPermesso}
            exportBusy={exportBusy}
            onEsporta={() => void downloadXlsx()}
            layout={layout}
          />
        </section>
      )}

      <StorniScoperti
        righe={scoperti}
        onApri={(ordine) => {
          setTab("tutte");
          setQuery(ordine);
        }}
      />

      {isMobile && (
        <div className="sticky -top-3 z-20 -mx-3 mb-3 flex items-center gap-2 bg-paper px-3 py-2">
          <input
            type="search"
            enterKeyHint="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ordine, cliente, n. documento"
            aria-label="Cerca proposte"
            className="h-12 min-w-0 flex-1 rounded-full border border-line-2 bg-card px-4 text-[16px] text-ink outline-none placeholder:text-ink-3 focus:border-ink"
          />
          <button
            type="button"
            onClick={() => setFiltriAperti(true)}
            aria-label={filtriFoglio > 0 ? `Filtri, ${filtriFoglio} attivi` : "Filtri"}
            className={[
              "om-press relative grid h-12 w-12 shrink-0 place-items-center rounded-full border",
              filtriFoglio > 0 ? "border-ink bg-ink text-on-ink" : "border-line-2 bg-card text-ink",
            ].join(" ")}
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M4 6h16M7 12h10M10 18h4" />
            </svg>
            {filtriFoglio > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-2 px-1 text-[11px] font-bold text-white ring-2 ring-paper">
                {filtriFoglio}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Sopra lo slot di stato: connesso = una riga sottile, non connesso =
          striscia ambra con l'azione (l'unico caso in cui serve spazio). */}
      <EurekaConnection />

      {syncMsg && (
        <div
          role="status"
          className="mb-3 rounded-md border border-line bg-paper-2 px-3 py-2 text-[12.5px] text-ink-2"
        >
          {syncMsg}
        </div>
      )}

      <ErrorBanner message={error} className="mb-3" />

      {/* Emissione in corso avviata prima: la promessa «chiudere non ferma il
          batch» vale solo se il batch si ritrova. Questa è la strada di
          ritorno. */}
      {batchAperto && (
        <div
          role="status"
          className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-accent/40 bg-accent-soft px-3 py-2"
        >
          <span className="text-[12.5px] font-medium text-ink">
            Emissione in corso: {batchAperto.creati + batchAperto.falliti}/{batchAperto.totale}
          </span>
          <button
            type="button"
            onClick={() => setBatchDaGuardare(batchAperto.batch_id)}
            className="rounded-md border border-accent bg-accent-2 px-3 py-1 text-[12px] font-medium text-white"
          >
            Guarda
          </button>
        </div>
      )}

      {!isMobile && (
        <div className="mb-2 flex items-baseline gap-3 px-1">
          <h2 className="font-display text-[20px] font-bold tracking-[-0.01em] text-ink">
            {ETICHETTE_TAB[tab]}
          </h2>
          {poiDellaCoda(tab, scoperti.length) && (
            <span className="text-[13px] text-ink-2">{poiDellaCoda(tab, scoperti.length)}</span>
          )}
          {rows.length > 0 && (
            <span className="ml-auto text-[12.5px] tabular-nums text-ink">
              {rows.length} {rows.length === 1 ? "riga" : "righe"}
              {tabTotal != null && !filtroAttivo && tabTotal > rows.length ? ` su ${tabTotal}` : ""}
            </span>
          )}
        </div>
      )}

      {!isMobile && barraSelezione}

      {loading && rows.length === 0 ? (
        <Card className="py-8 text-center">
          <p className="text-[13px] text-ink-2">Caricamento…</p>
        </Card>
      ) : rows.length === 0 ? (
        // Lo stato vuoto dice PERCHÉ è vuoto: con un filtro attivo «nessuna
        // proposta» è quasi sempre il filtro, non la realtà — e senza dirlo si
        // finisce a premere «Sincronizza» cercando righe che ci sono già.
        <Card className="py-8 text-center">
          <h3 className="mb-1 font-display text-[17px] font-bold text-ink">Nessuna riga</h3>
          {debouncedQuery || channelFilter || range.from || range.to ? (
            <>
              <p className="text-[12px] text-ink-2">
                Nessuna proposta in «{ETICHETTE_TAB[tab]}» con i filtri attivi.
              </p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setChannelFilter("");
                  setRange(RANGE_VUOTO);
                }}
                className="mt-2 text-[12px] text-accent underline-offset-2 hover:underline"
              >
                Azzera ricerca, canale e periodo
              </button>
            </>
          ) : (
            <p className="text-[12px] text-ink-2">
              Nessuna proposta in questo tab. Le righe nuove arrivano dal giro automatico ogni 15
              minuti, o subito con «⟳ Sincronizza».
            </p>
          )}
        </Card>
      ) : (
        <>
          {isTruncated && (
            <div role="status" className="mb-2 px-1 text-[12.5px] text-ink-2">
              {!filtroAttivo && tabTotal != null && tabTotal > rows.length
                ? `Mostrate ${rows.length} di ${tabTotal} righe`
                : `Mostrate le prime ${rows.length} righe: oltre potrebbero essercene altre`}{" "}
              — affina la ricerca o i filtri, oppure usa «Esporta Excel».
            </div>
          )}
          {isMobile ? (
            <>
              <div className="mb-2 flex min-h-[44px] items-center justify-between">
                {selectableRows.length > 0 ? (
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-[15px] font-medium text-ink">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = selected.size > 0 && !allSelected;
                      }}
                      onChange={toggleAll}
                      aria-label="Seleziona tutte le righe selezionabili"
                      className="size-[22px] cursor-pointer accent-accent-2"
                    />
                    Seleziona tutte
                  </label>
                ) : (
                  <span />
                )}
                <span className="text-[14px] tabular-nums text-ink">
                  {rows.length} {rows.length === 1 ? "riga" : "righe"}
                </span>
              </div>
              {rows.map((r) => (
                <ProposalCardMobile
                  key={r.id}
                  proposal={r}
                  selezionabile={selezionabile(r, tab)}
                  selezionata={selected.has(r.id)}
                  onToggle={toggleRow}
                  azioni={renderCell(AZIONI_COL, r)}
                />
              ))}
              {barraSelezione}
            </>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-line bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)]">
              <table
                className="text-[12.5px]"
                style={{ tableLayout: "fixed", width: totalWidth, minWidth: "100%" }}
              >
                <colgroup>
                  {layout.visible.map((c) => (
                    <col key={c.key} style={{ width: layout.widthOf(c.key) }} />
                  ))}
                </colgroup>
                <thead className="border-b border-line-2 bg-paper-2/60 text-[12px] text-ink">
                  <tr className="divide-x divide-line/60">
                    {layout.visible.map((col) => {
                      const sortable = col.sortField !== null;
                      const active = sortable && sort === col.sortField;
                      const alignCls = col.align === "right" ? "text-right" : "text-left";
                      return (
                        <th
                          key={col.key}
                          draggable
                          onDragStart={(e) => layout.onDragStart(col.key, e)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => layout.onDropOn(col.key, e)}
                          aria-sort={
                            active ? (direction === "asc" ? "ascending" : "descending") : undefined
                          }
                          title={`${col.label} — trascina per spostare${sortable ? ", clicca per ordinare" : ""}`}
                          className={`relative select-none px-0 py-0 font-semibold ${alignCls} cursor-grab active:cursor-grabbing`}
                        >
                          {col.key === "sel" ? (
                            <span className="block px-3 py-2.5">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                // Parziale ≠ tutto: senza questo la casella di
                                // testa direbbe «niente selezionato» con 5 righe
                                // spuntate.
                                ref={(el) => {
                                  if (el) el.indeterminate = selected.size > 0 && !allSelected;
                                }}
                                disabled={selectableRows.length === 0}
                                onChange={toggleAll}
                                aria-label="Seleziona tutte le righe selezionabili"
                                className="size-[15px] cursor-pointer accent-accent-2 align-middle disabled:opacity-40"
                              />
                            </span>
                          ) : sortable ? (
                            <button
                              type="button"
                              onClick={() => toggleSort(col.sortField as SortField)}
                              className={`w-full truncate px-3 py-2.5 ${alignCls} hover:underline ${active ? "underline decoration-2 underline-offset-4" : ""}`}
                            >
                              {col.label}
                              {active ? (direction === "asc" ? " ↑" : " ↓") : ""}
                            </button>
                          ) : (
                            <span className={`block truncate px-3 py-2.5 ${alignCls}`}>
                              {col.label}
                            </span>
                          )}
                          {/* Maniglia resize: bordo destro dell'header. */}
                          <span
                            role="presentation"
                            draggable={false}
                            onMouseDown={(e) => layout.startResize(col.key, e)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute right-0 top-0 z-10 h-full w-[7px] cursor-col-resize hover:bg-accent/30"
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    // Zebra su bianco + griglia verticale (richiesta Davide 15/07);
                    // la zebra cede il posto al rosso/ambra quando il documento è
                    // fuori termine SdI (rowToneSdi).
                    <tr
                      key={r.id}
                      className={`divide-x divide-line/40 border-t border-line/40 ${rowToneSdi(r)}`}
                    >
                      {layout.visible.map((col) => (
                        <td
                          key={col.key}
                          className={`overflow-hidden px-3 py-2 text-ink ${
                            col.align === "right" ? "text-right" : ""
                          }`}
                        >
                          {renderCell(col, r)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {isMobile && (
        <>
          <AzioniBarra>
            {puoFatturare && (
              <TastoBarra
                label="Sincronizza la fatturazione"
                busy={syncBusy}
                onClick={() => void runSyncNow()}
              >
                <svg
                  className="h-[22px] w-[22px]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 11a8 8 0 0 0-14.9-3.5M4 4v4h4M4 13a8 8 0 0 0 14.9 3.5M20 20v-4h-4" />
                </svg>
              </TastoBarra>
            )}
            <TastoBarra label="Altri strumenti" onClick={() => setStrumentiAperti(true)}>
              <svg className="h-[22px] w-[22px]" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="5" cy="12" r="1.8" fill="currentColor" />
                <circle cx="12" cy="12" r="1.8" fill="currentColor" />
                <circle cx="19" cy="12" r="1.8" fill="currentColor" />
              </svg>
            </TastoBarra>
          </AzioniBarra>

          <FogliTelefono
            filtriAperti={filtriAperti}
            setFiltriAperti={setFiltriAperti}
            strumentiAperti={strumentiAperti}
            setStrumentiAperti={setStrumentiAperti}
            channels={channels}
            channelFilter={channelFilter}
            setChannelFilter={setChannelFilter}
            range={range}
            setRange={setRange}
            campoData={campoData}
            setCampoData={setCampoData}
            righe={rows.length}
            exportBusy={exportBusy}
            downloadXlsx={() => void downloadXlsx()}
            setManualOpen={setManualOpen}
            senzaPermesso={senzaPermesso}
          />
        </>
      )}

      {markDialog && (
        <MarkDialog
          proposal={markDialog.proposal}
          forceStatus={markDialog.forceStatus}
          onClose={() => setMarkDialog(null)}
          onDone={() => {
            setMarkDialog(null);
            void load();
          }}
        />
      )}
      {excludeTarget && (
        <ExcludeDialog
          proposal={excludeTarget}
          onClose={() => setExcludeTarget(null)}
          onDone={() => {
            setExcludeTarget(null);
            void load();
          }}
        />
      )}
      {ncTarget && (
        <NcConfirmDialog
          proposal={ncTarget}
          onClose={() => setNcTarget(null)}
          onDone={() => {
            setNcTarget(null);
            void load();
          }}
        />
      )}
      {editTarget && (
        <EditDialog
          proposal={editTarget}
          onClose={() => setEditTarget(null)}
          onDone={() => {
            setEditTarget(null);
            void load();
          }}
        />
      )}
      {(createOpen || batchDaGuardare) && (
        <CreateDocumentsDialog
          proposals={selectedRows}
          batchIniziale={batchDaGuardare ?? undefined}
          onClose={() => {
            setCreateOpen(false);
            setBatchDaGuardare(null);
            void caricaBatchAperto();
          }}
          onDone={() => {
            setCreateOpen(false);
            setBatchDaGuardare(null);
            setSelected(new Set());
            void caricaBatchAperto();
            void load();
          }}
          // La riga bloccata si corregge da lì: si chiude l'anteprima (stesso
          // z-index, altrimenti la Modifica finirebbe sotto) e si torna in
          // pagina, dove «Fattura» si riapre con UN click perché la selezione
          // non si è persa — i filtri non cambiano, quindi il batch resta
          // spuntato (prima erano ~6 click e la selezione da rifare).
          onCorreggi={(id) => {
            const riga = selectedRows.find((p) => p.id === id);
            if (!riga) return;
            setCreateOpen(false);
            setEditTarget(riga);
          }}
        />
      )}
      {manualOpen && (
        <ManualDialog
          onClose={() => setManualOpen(false)}
          onDone={() => {
            setManualOpen(false);
            void load();
          }}
        />
      )}
    </main>
  );
}
